import { Platform, Share } from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import type { CustomerOrderDetail } from '../../orders/domain/orderTypes';
import type { KitchenTicket } from '../../hospitality/domain/hospitalityTypes';
import { buildOrderComanda } from '../../orders/screens/components/buildOrderComanda';
import { buildKitchenTicketComanda } from '../../hospitality/screens/buildKitchenTicketComanda';
import { buildPreCuenta, type PreCuentaLine } from '../../hospitality/screens/buildPreCuenta';
import { isPrintUserCancellation, printThermalDocument } from './printThermalDocument';
import { getBranchReceiptPrintConfig } from '../infra/printConfigRepo';
import {
  completeKitchenPrintJob,
  enqueueKitchenPrintJob,
  type KitchenPrintJob,
} from '../infra/kitchenPrintJobsRepo';

/** Comandas van a `kitchenPrinterName` si está configurada; si no, a la impresora default. */
async function kitchenPrinterOverride(branchId: string): Promise<string | undefined> {
  const config = await getBranchReceiptPrintConfig(branchId);
  return config.kitchenPrinterName || undefined;
}

type PreCuentaShareArgs = {
  tableName: string;
  covers?: number | null;
  lines: PreCuentaLine[];
  subtotal: number;
  discountTotal?: number;
  tipAmount?: number;
  total: number;
  openedAt?: string | null;
  context?: 'mesa' | 'counter';
};

function isShareUserCancellation(message: string): boolean {
  return message.includes('Share canceled') || message.includes('Share cancelled');
}

function preCuentaShareTitle(args: PreCuentaShareArgs): string {
  if (args.context === 'counter') return 'Resumen del pedido';
  return `Cuenta ${args.tableName}`;
}

export async function printKitchenComanda(args: {
  branchId: string;
  ticket: KitchenTicket;
  tableName?: string | null;
  stationId?: string | null;
  printerName?: string | null;
  useAgent?: boolean;
}): Promise<void> {
  const document = buildKitchenTicketComanda(args.ticket, { tableName: args.tableName });
  const printerName = args.printerName?.trim() || await kitchenPrinterOverride(args.branchId);
  const dedupeKey = `${args.ticket.id}:${args.stationId ?? 'default'}:${Math.floor(Date.now() / 1000)}`;
  const jobId = await enqueueKitchenPrintJob({
    branchId: args.branchId,
    ticketId: args.ticket.id,
    stationId: args.stationId,
    printerName,
    dedupeKey,
    payload: { text: document.text, html: document.html },
  });
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await printThermalDocument(args.branchId, document, {
        // Comandas respetan mode='off': sin esto el fallback abría el diálogo
        // de impresión del navegador por cada ticket nuevo (spam en cocina
        // cuando la sucursal desactivó la impresión).
        blockWhenModeOff: true,
        transportOverride: args.useAgent ? 'agent' : undefined,
        fallbackToBrowser: args.useAgent ? false : undefined,
        printerOverride: printerName,
      });
      await completeKitchenPrintJob({ jobId, status: 'printed' });
      return;
    } catch (error) {
      lastError = error;
      const message = error instanceof Error ? error.message : 'No se pudo imprimir la comanda.';
      if (attempt === 0) {
        const nextAttemptAt = new Date(Date.now() + 1_000).toISOString();
        await completeKitchenPrintJob({ jobId, status: 'retrying', error: message, nextAttemptAt }).catch(() => undefined);
        await new Promise((resolve) => setTimeout(resolve, 1_000));
        continue;
      }
      await completeKitchenPrintJob({ jobId, status: 'failed', error: message }).catch(() => undefined);
    }
  }
  throw lastError instanceof Error ? lastError : new Error('No se pudo imprimir la comanda.');
}

/** Reintenta un job fallido/pendiente imprimiendo el payload guardado. */
export async function retryKitchenPrintJob(args: {
  branchId: string;
  job: KitchenPrintJob;
}): Promise<void> {
  const { job } = args;
  const text = typeof job.payload?.text === 'string' ? job.payload.text : '';
  const html = typeof job.payload?.html === 'string' ? job.payload.html : text;
  if (!text) throw new Error('El job no tiene contenido para imprimir.');
  const printerName = job.printerName?.trim() || await kitchenPrinterOverride(args.branchId);
  try {
    await printThermalDocument(args.branchId, { text, html }, { printerOverride: printerName });
    await completeKitchenPrintJob({ jobId: job.id, status: 'printed' });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'No se pudo imprimir la comanda.';
    await completeKitchenPrintJob({ jobId: job.id, status: 'failed', error: message }).catch(() => undefined);
    throw error instanceof Error ? error : new Error(message);
  }
}

export async function printKitchenComandaSafe(args: {
  branchId: string;
  ticket: KitchenTicket;
  tableName?: string | null;
  stationId?: string | null;
  printerName?: string | null;
  useAgent?: boolean;
  onError: (title: string, message: string) => void;
}): Promise<void> {
  try {
    await printKitchenComanda(args);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'No se pudo imprimir';
    if (!isPrintUserCancellation(msg)) {
      args.onError('Comanda', msg);
    }
  }
}

export async function printOrderComanda(args: {
  branchId: string;
  order: CustomerOrderDetail;
}): Promise<void> {
  const document = buildOrderComanda(args.order);
  await printThermalDocument(args.branchId, document, {
    printerOverride: await kitchenPrinterOverride(args.branchId),
  });
}

export async function printOrderComandaSafe(args: {
  branchId: string;
  order: CustomerOrderDetail;
  onError: (title: string, message: string) => void;
}): Promise<void> {
  try {
    await printOrderComanda(args);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'No se pudo imprimir';
    if (!isPrintUserCancellation(msg)) {
      args.onError('Comanda', msg);
    }
  }
}

export async function printPreCuentaDocument(args: {
  branchId: string;
  tableName: string;
  covers?: number | null;
  lines: PreCuentaLine[];
  subtotal: number;
  discountTotal?: number;
  tipAmount?: number;
  total: number;
  openedAt?: string | null;
  context?: 'mesa' | 'counter';
}): Promise<void> {
  const document = buildPreCuenta({
    tableName: args.tableName,
    covers: args.covers,
    lines: args.lines,
    subtotal: args.subtotal,
    discountTotal: args.discountTotal,
    tipAmount: args.tipAmount,
    total: args.total,
    openedAt: args.openedAt,
    context: args.context,
  });
  await printThermalDocument(args.branchId, document);
}

export async function printPreCuentaDocumentSafe(args: {
  branchId: string;
  tableName: string;
  covers?: number | null;
  lines: PreCuentaLine[];
  subtotal: number;
  discountTotal?: number;
  tipAmount?: number;
  total: number;
  openedAt?: string | null;
  context?: 'mesa' | 'counter';
  onError: (title: string, message: string) => void;
}): Promise<void> {
  try {
    await printPreCuentaDocument(args);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'No se pudo imprimir';
    if (!isPrintUserCancellation(msg)) {
      const title = args.context === 'counter' ? 'Resumen del pedido' : 'Cuenta';
      args.onError(title, msg);
    }
  }
}

/** PDF / Share / clipboard sobre el mismo documento que la precuenta impresa. */
export async function sharePreCuentaDocument(args: PreCuentaShareArgs): Promise<'shared' | 'copied'> {
  const document = buildPreCuenta({
    tableName: args.tableName,
    covers: args.covers,
    lines: args.lines,
    subtotal: args.subtotal,
    discountTotal: args.discountTotal,
    tipAmount: args.tipAmount,
    total: args.total,
    openedAt: args.openedAt,
    context: args.context,
  });
  const title = preCuentaShareTitle(args);

  if (Platform.OS === 'web') {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title, text: document.text });
      return 'shared';
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(document.text);
      return 'copied';
    }
    await Share.share({ message: document.text, title });
    return 'shared';
  }

  const canShare = await Sharing.isAvailableAsync();
  if (canShare) {
    const { uri } = await Print.printToFileAsync({ html: document.html });
    await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: title });
    return 'shared';
  }
  await Share.share({ message: document.text, title });
  return 'shared';
}

export async function sharePreCuentaDocumentSafe(
  args: PreCuentaShareArgs & {
    onError: (title: string, message: string) => void;
    onCopied?: () => void;
  }
): Promise<void> {
  const title = args.context === 'counter' ? 'Resumen del pedido' : 'Cuenta';
  try {
    const result = await sharePreCuentaDocument(args);
    if (result === 'copied') args.onCopied?.();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'No se pudo compartir';
    if (!isShareUserCancellation(msg)) {
      args.onError(title, msg);
    }
  }
}
